import { Col, Form, Button } from "react-bootstrap";
import { useEffect } from "react";
import { useActuatorConfigurationContext } from "context/ActuatorConfigurationContext";
import MinuteSecondPicker, {
    MinuteSecondPickerProps,
} from "components/common/MinuteSecondPicker";
import { Actuator, ActuatorTiming } from "types/Actuator";
import { useParams } from "react-router-dom";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

const defaultPairing: ActuatorTiming = [
    [0, 0],
    [0, 0],
];

export const RawCyclesInput = ({
    timing,
    ldsuId,
    editedActuator,
    variant = "default",
    onChange = () => { },
}: {
    timing: ActuatorTiming;
    ldsuId: string;
    editedActuator: Actuator;
    variant?: "default" | "light";
    onChange?: (timing: ActuatorTiming) => void;
}) => {
    const minuteSecondPickerProps: MinuteSecondPickerProps = {
        editedActuator,
        hasCycles: true,
        cycleIndex: 0,
        onChange,
    };

    const handleRemovePair = (index: number) => {
        const newTiming = timing.filter((pair, i: number) => i !== index);
        onChange(newTiming);
    };

    const handleAddPair = () => {
        const newTiming: ActuatorTiming = [...timing, [0, 0]];
        onChange(newTiming);
    };

    const renderPairs = (pairs: ActuatorTiming) => {
        if (!pairs) {
            return;
        }

        if (pairs) {
            return pairs.map((p, index: number) => {
                const i = index + 1;

                return (
                    <Form.Row className="mt-4 mb-1" key={`${ldsuId}-${index}`}>
                        <Col md={11}>
                            <MinuteSecondPicker
                                {...{
                                    ...minuteSecondPickerProps,
                                    cycleIndex: i,
                                }}
                                variant={variant}
                            />
                        </Col>

                        <Col md={1} className="d-flex align-items-center justify-content-center">
                            {pairs.length > 2 && (
                                <CloseOutlined
                                    onClick={() => handleRemovePair(index)}
                                    onKeyDown={() => handleRemovePair(index)}
                                    className="cursor-pointer text-primary-red-1"
                                />
                            )}
                        </Col>
                    </Form.Row>
                );
            });
        }
    };

    return (
        <>
            {renderPairs(timing)}
            <Form.Row className="mt-5 mb-5">
                <Col>
                    <Button
                        className="mr-3"
                        onClick={handleAddPair}
                        disabled={(timing || defaultPairing).length >= 8}
                    >
                        + More
                    </Button>{" "}
                    (Maximum 8 Pairs)
                </Col>
            </Form.Row>
        </>
    );
};

const CyclesInput = () => {
    const {
        editedActuator,
        editedActuator: { timing },
        setEditedActuator,
    } = useActuatorConfigurationContext() as {
        editedActuator: Actuator;
        setEditedActuator: React.Dispatch<React.SetStateAction<Actuator>>;
    };

    const params: { ldsuId: string } = useParams();
    const { ldsuId } = params;

    useEffect(() => {
        setEditedActuator((prevState: Actuator) => ({
            ...prevState,
            timing: timing && timing?.length >= 2 ? timing : defaultPairing,
        }));
    }, [timing, setEditedActuator]);

    const handleTimingChange = (newTiming: ActuatorTiming) => {
        setEditedActuator((prevState: Actuator) => ({
            ...prevState,
            timing: newTiming,
        }));
    };

    return (
        <RawCyclesInput
            timing={timing ?? defaultPairing}
            ldsuId={ldsuId}
            editedActuator={editedActuator}
            onChange={handleTimingChange}
        />
    );
};

export default CyclesInput;
